import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, Loader2, Search } from "lucide-react";
import { findProvidersByIdentifierFn } from "@/lib/providers-secure.functions";
import { buildProviderSlug } from "@/lib/provider-slug";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Field = "name" | "npi" | "license" | "phone" | "address";

const FIELDS: { id: Field; label: string }[] = [
  { id: "name", label: "Name" },
  { id: "npi", label: "NPI" },
  { id: "license", label: "License" },
  { id: "phone", label: "Phone" },
  { id: "address", label: "Address" },
];

type Suggestion = {
  id: string;
  name: string | null;
  practice: string | null;
  specialty: string | null;
  city: string | null;
  state: string | null;
};

export function FindMyPracticeButton({ className = "" }: { className?: string }) {
  const [field, setField] = useState<Field>("name");
  const [value, setValue] = useState("");
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const reqId = useRef(0);

  // Debounced fetch
  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const { rows } = await findProvidersByIdentifierFn({ data: { query: q, limit: 8 } });
        if (id !== reqId.current) return;
        setItems(rows as Suggestion[]);
        setOpen(true);
      } catch {
        if (id === reqId.current) setItems([]);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submit = () => {
    const q = value.trim();
    if (!q) return;
    setOpen(false);
    navigate({ to: "/search", search: { q } });
  };

  const pick = (s: Suggestion) => {
    setOpen(false);
    setValue(s.name || s.practice || "");
    navigate({
      to: "/providers/$id",
      params: { id: buildProviderSlug(s) },
    });
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        role="search"
        aria-label="Find my practice"
        className="group flex items-stretch bg-background border border-border/80 hover:border-border rounded-lg w-full overflow-hidden shadow-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all h-9"
      >
        <div className="flex items-center pl-2.5 pr-1 shrink-0 text-muted-foreground/80 group-focus-within:text-primary transition-colors">
          <Search className="h-3.5 w-3.5" />
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => items.length && setOpen(true)}
          placeholder="Find my practice..."
          autoComplete="off"
          suppressHydrationWarning
          className="flex-1 min-w-0 bg-transparent py-1 pr-2 text-[13px] text-foreground placeholder:text-muted-foreground outline-none !shadow-none !ring-0 focus:!outline-none focus:!ring-0 focus:!shadow-none focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!shadow-none border-0"
        />
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger
            type="button"
            className="flex items-center justify-between gap-1 w-[92px] shrink-0 border-l border-border/60 bg-muted/30 hover:bg-muted/60 pl-2.5 pr-2 py-1 text-[12px] font-medium text-foreground outline-none !shadow-none !ring-0 focus:!outline-none focus:!ring-0 focus:!shadow-none focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!shadow-none transition-colors h-full cursor-pointer"
            aria-label="Search by"
          >
            <span className="truncate">{FIELDS.find((f) => f.id === field)?.label || "Name"}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[130px] z-[100] rounded-xl border border-border/80 shadow-lg p-1">
            {FIELDS.map((f) => (
              <DropdownMenuItem
                key={f.id}
                onClick={() => setField(f.id)}
                className={`text-[12px] cursor-pointer rounded-md my-0.5 ${field === f.id ? "font-semibold bg-primary/10 text-primary" : ""}`}
              >
                {f.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </form>

      {open && (loading || items.length > 0) && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-popover text-popover-foreground rounded-xl border border-border/80 shadow-xl max-h-80 overflow-auto overflow-x-hidden">
          {loading && items.length === 0 && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> Searching…
            </div>
          )}
          {items.map((s) => (
            <button
              type="button"
              key={s.id}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className="w-full text-left px-4 py-2.5 hover:bg-primary/10 border-b border-border/60 last:border-b-0 transition-colors cursor-pointer"
            >
              <div className="text-sm font-medium text-foreground truncate">
                {s.name || s.practice || "Unnamed provider"}
              </div>
              <div className="text-xs text-muted-foreground truncate mt-0.5">
                {[s.specialty, [s.city, s.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
          {!loading && value.trim().length >= 2 && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); submit(); }}
              className="w-full text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-primary hover:bg-primary/10 transition-colors cursor-pointer"
            >
              See all results for "{value.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
